import type { RendererProps } from '@atlaskit/renderer';
import React, { lazy, memo, Suspense } from 'react';
import { emptyADFContent } from '@canvas/adf/empty';
import { type RichTextReadContent } from './rich-text-field-read-module.ts';
import styles from './rich-text-value-view.module.css';

const RichTextRenderer = lazy(() =>
  import('./rich-text-renderer.tsx').then((module) => ({
    default: module.RichTextRenderer
  }))
);

export type RichTextPresentation = Pick<RendererProps, 'dataProviders' | 'eventHandlers'>;

/**
 * Field-owned stateless presentation; the host supplies provider resources and navigation.
 */
export const RichTextValueView = memo(function RichTextValueView({
  content,
  showDisabledValue = false,
  ...presentation
}: RichTextPresentation & {
  readonly content: RichTextReadContent;
  readonly showDisabledValue?: boolean;
}) {
  /**
   * The resolver returns this shared sentinel only for its empty fallback. Do not
   * scan stored ADF: empty-looking nodes can still carry layout or rich content.
   */
  if (content.document === emptyADFContent) {
    return (
      <div className={styles.empty} aria-hidden="true">
        &nbsp;
      </div>
    );
  }

  return (
    <Suspense fallback={null}>
      <RichTextRenderer
        content={content}
        showDisabledValue={showDisabledValue}
        {...presentation}
      />
    </Suspense>
  );
});
